import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CreditCard, ArrowLeft, RefreshCw } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import api, { formatApiError } from "../lib/api";

const VALID_PLANS = new Set(["starter", "growth", "pro"]);

export default function CheckoutStart() {
  const { plan } = useParams();
  const started = useRef(false);
  const [error, setError] = useState("");

  const openCheckout = useCallback(async () => {
    setError("");
    try {
      const { data: status } = await api.get("/billing/stripe/status");
      if (!status.checkout) {
        throw new Error("Online checkout is still being configured. Your account is ready, but payment cannot be completed yet.");
      }
      const { data: session } = await api.post("/billing/stripe/checkout", { plan });
      window.location.assign(session.url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "Checkout could not be opened.");
    }
  }, [plan]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!VALID_PLANS.has(plan)) {
      setError("This plan is not available for online checkout.");
      return;
    }
    openCheckout();
  }, [plan, openCheckout]);

  return <AuthLayout>
    <div data-testid="checkout-start-page">
      <div className="mb-5 flex h-11 w-11 items-center justify-center border border-slate-200 bg-slate-50">
        <CreditCard className="h-5 w-5 text-slate-700" />
      </div>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
        {error ? "Checkout needs attention" : "Opening secure checkout"}
      </h1>
      <p className="mt-2 text-sm leading-6 text-slate-500">
        {error || `Your ${plan?.charAt(0).toUpperCase() + plan?.slice(1)} plan is selected. We are connecting your CleanIQ account to Stripe.`}
      </p>

      {!error && <div className="mt-8 h-1.5 overflow-hidden bg-slate-100">
        <div className="h-full w-2/3 animate-pulse bg-slate-900" />
      </div>}

      {error && <div className="mt-7 space-y-3">
        {VALID_PLANS.has(plan) && <button onClick={openCheckout} data-testid="checkout-retry-button"
          className="flex w-full items-center justify-center gap-2 bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-700">
          <RefreshCw className="h-4 w-4" /> Try checkout again
        </button>}
        <Link to="/billing" className="flex w-full items-center justify-center border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          View plans and billing
        </Link>
      </div>}

      <Link to="/#pricing" className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-slate-600 underline underline-offset-4">
        <ArrowLeft className="h-4 w-4" /> Back to pricing
      </Link>
    </div>
  </AuthLayout>;
}
